import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { getHighScore } from "@/lib/highscore";
import { sfx, resumeAudio } from "@/lib/sfx";
import { Zap, ArrowRight, Trophy, HelpCircle, Cpu, Box, Grid3x3, Crown, Lock, CalendarClock, Wind, Palette, Truck, Snowflake, Flame, Bell, BellOff, Award, GitBranch, Sparkles, CloudRain, Coins, Sun, CloudFog, Dice5, UserCircle2, Swords } from "lucide-react";
import HowToPlayDialog from "@/components/HowToPlayDialog";
import PaywallDialog from "@/components/PaywallDialog";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import SkinPicker from "@/components/SkinPicker";
import AchievementsDialog from "@/components/AchievementsDialog";
import SeasonTrackDialog from "@/components/SeasonTrackDialog";
import ShopDialog from "@/components/ShopDialog";
import { getIsPro, getEmail, refreshProStatus, setIsPro, getIsSeason, getSeasonExpiresAt, getPlan } from "@/lib/pro";
import { getChips } from "@/lib/chips";
import { getCurrentSkin } from "@/lib/skins";
import { getStreak } from "@/lib/streak";
import { getLifeTokens, armDuel } from "@/lib/duel";
import { getWeather, setWeather, WEATHER_LABELS } from "@/lib/weather";
import { isRouletteAvailable, spinRoulette } from "@/lib/roulette";
import { isRoulette5xPending, getRouletteStreak, getYesterdayRouletteRecap } from "@/lib/achievementsExtra";
import { getVault, msUntilUnlock, isVaultUnlocked, claimEarly, claimUnlocked, formatLockRemaining, JACKPOT_BONUS_MULT, JACKPOT_BONUS_MULT_FRENZY, isVaultFrenzy, currentBonusMult, frenzyStatus, relockVaultFrenzy } from "@/lib/jackpotVault";
import { addChips as addChipsMenu } from "@/lib/chips";
import { getEmail as getMenuEmail } from "@/lib/pro";
import HandleDialog, { getPublicHandle } from "@/components/HandleDialog";
import TradeHallDialog from "@/components/TradeHallDialog";
import ClanDialog from "@/components/ClanDialog";
import { getCachedClan } from "@/lib/clan";
import { optInReminder, optOutReminder, reminderOptedIn, reminderSupported, maybeFireDailyReminder } from "@/lib/reminder";
import { ACHIEVEMENTS, getUnlockedCount, refreshPassiveAchievements } from "@/lib/achievements";
import { enablePush, disablePush, pushSubscribed, pushSupported } from "@/lib/push";
import { syncSeasonTrack, currentDay } from "@/lib/seasonRewards";
import { restorePlayerState } from "@/lib/playerSync";
import axios from "axios";
import { toast } from "sonner";
import { useT } from "@/i18n";

export default function MainMenu() {
  const nav = useNavigate();
  const highs = getHighScore();
  const [howToOpen, setHowToOpen] = useState(false);
  const [mode, setMode] = useState("2d"); // "2d" | "3d"
  const [isPro, setPro] = useState(getIsPro());
  const [seasonActive, setSeasonActive] = useState(() => getIsSeason());
  const [seasonExpires, setSeasonExpires] = useState(() => getSeasonExpiresAt());
  const [seasonPlan, setSeasonPlan] = useState(() => getPlan());
  const [paywall, setPaywall] = useState({ open: false, feature: "", defaultTab: "pro" });
  const [skinOpen, setSkinOpen] = useState(false);
  const [skin, setSkin] = useState(() => getCurrentSkin());
  const [streakInfo, setStreakInfo] = useState(() => getStreak());
  const [reminderOn, setReminderOn] = useState(() => reminderOptedIn());
  const [pushOn, setPushOn] = useState(false);
  const [achievementsOpen, setAchievementsOpen] = useState(false);
  const [achievementCount, setAchievementCount] = useState(() => getUnlockedCount());
  const [trackOpen, setTrackOpen] = useState(false);
  const [trackDay, setTrackDay] = useState(0);
  const [resumeRun, setResumeRun] = useState(null);
  const [shopOpen, setShopOpen] = useState(false);
  const [chipBalance, setChipBalance] = useState(() => getChips());
  const [weather, setWeatherState] = useState(() => getWeather());
  const [rouletteReady, setRouletteReady] = useState(() => isRouletteAvailable());
  const [handleOpen, setHandleOpen] = useState(false);
  const [publicHandle, setPublicHandleState] = useState(() => getPublicHandle());
  const [tradeOpen, setTradeOpen] = useState(false);
  const [clanOpen, setClanOpen] = useState(false);
  const [cachedClan, setCachedClanState] = useState(() => getCachedClan());
  const [challenge, setChallenge] = useState(null); // { from, score, level }
  const loc = useLocation();
  const { t } = useT();

  // Parse ?duel_from=&duel_score=&duel_level= on mount → show challenge banner
  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const from = (params.get("duel_from") || "").trim().slice(0, 20);
    const scoreNum = parseInt(params.get("duel_score") || "0", 10);
    const lvl = (params.get("duel_level") || "").trim().slice(0, 20);
    if (from && scoreNum > 0) {
      setChallenge({ from, score: scoreNum, level: lvl || "daily" });
      // Clean URL so refresh doesn't re-trigger — replace, don't push
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, "", cleanUrl);
    }
  }, [loc.search]);

  useEffect(() => {
    // Silent refresh: if we have an email, re-check backend pro/season status on load
    const email = getEmail();
    if (email) {
      // Restore server-side progress first, then hydrate season status
      restorePlayerState().then(() => refreshProStatus(email)).then((r) => {
        setPro(!!r.is_pro);
        setSeasonActive(!!r.is_season);
        setSeasonExpires(r.season_expires_at || null);
        setSeasonPlan(r.plan || null);
        syncSeasonTrack();
        setTrackDay(currentDay());
      });
      // Check for active-run resume snapshot (cross-device)
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/player/restore/${encodeURIComponent(email)}`)
        .then((res) => {
          const ar = res?.data?.active_run;
          // Only surface a resume banner if run has a level, isn't cleared, and was saved recently
          if (ar && ar.level && !ar.cleared_at && ar.saved_at) {
            const ageMin = (Date.now() - new Date(ar.saved_at).getTime()) / 60000;
            if (ageMin < 30) setResumeRun(ar);
          }
        }).catch(() => {});
    } else {
      // No email → still sync (will clear track if no local season flag either)
      syncSeasonTrack();
      setTrackDay(currentDay());
    }
    // Nudge notification if user opted in and hasn't played today
    maybeFireDailyReminder({ streak: getStreak().streak });
    // Check passive achievements (streak, pro) that don't need a run to unlock
    refreshPassiveAchievements();
    setAchievementCount(getUnlockedCount());
    // Check current push subscription status
    if (pushSupported()) {
      pushSubscribed().then((v) => setPushOn(!!v));
    }
  }, []);

  const start = (level) => {
    resumeAudio();
    // Daily Challenge is free for everyone (no 3D or Pro gate — it's the hook).
    if (level === "daily") {
      sfx.click();
      nav(mode === "3d" && isPro ? "/play3d/daily" : "/play/daily");
      return;
    }
    // Neon Rain is Season Pass exclusive
    if (level === "neon_rain" && !seasonActive) {
      sfx.click();
      setPaywall({ open: true, feature: t("paywall.feature_neon_rain") || "The Neon Rain bonus stage", defaultTab: "season" });
      return;
    }
    // Rooftop, Highway, Snowstorm, Tunnels, Beach, Underground → open Season tab by default (revenue-optimized)
    if (!isPro && (mode === "3d" || level === "underground" || level === "rooftop" || level === "highway" || level === "snowstorm" || level === "tunnels" || level === "beach")) {
      sfx.click();
      let feature;
      if (level === "tunnels") feature = t("paywall.feature_tunnels") || "The Tunnels stage";
      else if (level === "snowstorm") feature = t("paywall.feature_snowstorm") || "The Snowstorm stage";
      else if (level === "highway") feature = t("paywall.feature_highway") || "The Highway stage";
      else if (level === "rooftop") feature = t("paywall.feature_rooftop") || "The Rooftop stage";
      else if (level === "beach") feature = t("paywall.feature_beach") || "The Beach stage";
      else if (mode === "3d" && level === "underground") feature = t("paywall.feature_3d_underground");
      else if (mode === "3d") feature = t("paywall.feature_3d");
      else feature = t("paywall.feature_underground");
      setPaywall({ open: true, feature, defaultTab: "season" });
      return;
    }
    sfx.click();
    nav(mode === "3d" ? `/play3d/${level}` : `/play/${level}`);
  };

  // Format season expiry as "23h" or "5d" or "renews monthly"
  const formatSeasonExpiry = () => {
    if (seasonPlan === "season_monthly") return t("menu.season_monthly_label") || "MONTHLY";
    if (!seasonExpires) return "";
    try {
      const ms = new Date(seasonExpires).getTime() - Date.now();
      if (ms <= 0) return t("menu.season_expired") || "EXPIRED";
      const hours = Math.floor(ms / 3600000);
      if (hours < 48) return `${hours}${t("menu.h_short") || "h"}`;
      return `${Math.floor(hours / 24)}${t("menu.d_short") || "d"}`;
    } catch {
      return "";
    }
  };

  const togglePush = async () => {
    sfx.click();
    if (pushOn) {
      const res = await disablePush();
      setPushOn(false);
      if (res.status === "unsubscribed") toast.success(t("menu.push_off_toast") || "Push notifications OFF");
      return;
    }
    try {
      const res = await enablePush();
      if (res.status === "granted") {
        setPushOn(true);
        toast.success(t("menu.push_on_toast") || "Push notifications ON");
      } else if (res.status === "denied") {
        toast.error(t("menu.push_denied_toast") || "Notifications blocked — enable them in browser settings.");
      }
    } catch (e) {
      toast.error(t("menu.push_error_toast") || "Could not enable push notifications.");
    }
  };

  return (
    <div className="min-h-screen w-full grid-bg relative overflow-hidden" data-testid="main-menu">
      {/* Ambient orbs */}
      <div className="pointer-events-none absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full blur-3xl opacity-30" style={{ background: "radial-gradient(circle, #00F0FF, transparent 60%)" }} />
      <div className="pointer-events-none absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full blur-3xl opacity-25" style={{ background: "radial-gradient(circle, #FF0055, transparent 60%)" }} />

      {/* Challenge banner — someone shared a duel URL with this player */}
      {challenge && (
        <div
          data-testid="challenge-banner"
          className="absolute top-6 left-1/2 -translate-x-1/2 z-30 px-6 py-4 border border-[#F0ABFC] bg-black/85 backdrop-blur-xl flex items-center gap-4 max-w-[92vw]"
          style={{ boxShadow: "0 0 24px rgba(240,171,252,0.5)" }}
        >
          <Swords size={20} className="text-[#F0ABFC]" />
          <div className="flex flex-col">
            <div className="text-[10px] tracking-[0.4em] text-[#F0ABFC]">
              <span data-testid="challenge-from" className="text-white font-semibold">{challenge.from}</span> CHALLENGED YOU
            </div>
            <div className="text-xs text-white/80 tracking-wider">
              BEAT <span data-testid="challenge-score" className="text-[#39FF14] tabular-nums font-bold" style={{ textShadow: "0 0 8px rgba(57,255,20,0.5)" }}>
                {challenge.score.toLocaleString()}
              </span>
              {" "}on <span className="text-white/90 uppercase">{challenge.level}</span>
            </div>
          </div>
          <button
            data-testid="challenge-accept-button"
            onClick={() => {
              sfx.click();
              armDuel(challenge.from, challenge.score);
              const target = challenge.level === "daily" ? "/play/daily" : `/play/${challenge.level}`;
              setChallenge(null);
              nav(target);
            }}
            className="ml-2 px-4 py-2 border border-[#39FF14] text-[#39FF14] text-[10px] tracking-[0.3em] hover:bg-[#39FF14] hover:text-black transition-colors"
            style={{ boxShadow: "0 0 12px rgba(57,255,20,0.4)" }}
          >
            ACCEPT →
          </button>
          <button
            data-testid="challenge-dismiss-button"
            onClick={() => { sfx.click(); setChallenge(null); }}
            className="text-white/40 hover:text-white/80 text-lg leading-none px-2"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* Top-right: Pro badge / Upgrade CTA + Skin + Language switcher */}
      <div className="absolute top-6 right-8 z-20 flex items-center gap-3">
        <button
          data-testid="skin-picker-button"
          onClick={() => { sfx.click(); setSkinOpen(true); }}
          className="flex items-center gap-2 px-3 py-1.5 border text-[10px] tracking-[0.3em] transition-colors"
          style={{
            borderColor: skin.color,
            color: skin.color,
            boxShadow: `0 0 14px ${skin.glow}`,
          }}
        >
          <Palette size={12} /> {skin.name.split(" ")[0]}
        </button>
        <LanguageSwitcher />
        {seasonActive && (
          <div
            data-testid="season-badge"
            className="flex items-center gap-2 px-3 py-1.5 border border-[#F0ABFC] text-[#F0ABFC] text-[10px] tracking-[0.3em]"
            style={{ boxShadow: "0 0 16px rgba(240,171,252,0.4)" }}
            title={t("menu.season_active") || "Season Pass active"}
          >
            <Sparkles size={12} /> {t("menu.season_badge") || "SEASON"}
            <span className="text-white/70">· {formatSeasonExpiry()}</span>
          </div>
        )}
        {isPro ? (
          <div
            data-testid="pro-badge"
            className="flex items-center gap-2 px-3 py-1.5 border border-[#FFD400] text-[#FFD400] text-[10px] tracking-[0.3em]"
            style={{ boxShadow: "0 0 16px rgba(255,212,0,0.4)" }}
          >
            <Crown size={12} /> {t("menu.pro_badge")} · {(getEmail() || "").slice(0, 18)}
          </div>
        ) : (
          <button
            data-testid="upgrade-button"
            onClick={() => { sfx.click(); setPaywall({ open: true, feature: t("paywall.feature_full"), defaultTab: "pro" }); }}
            className="flex items-center gap-2 px-4 py-2 border border-[#FFD400] text-[#FFD400] text-[10px] tracking-[0.3em] hover:bg-[#FFD400] hover:text-black transition-colors"
            style={{ boxShadow: "0 0 16px rgba(255,212,0,0.35)" }}
          >
            <Crown size={12} /> {t("menu.upgrade_pro")}
          </button>
        )}
        {!seasonActive && (
          <button
            data-testid="get-season-button"
            onClick={() => { sfx.click(); setPaywall({ open: true, feature: t("paywall.feature_season") || "Season Pass perks", defaultTab: "season" }); }}
            className="flex items-center gap-2 px-4 py-2 border border-[#F0ABFC] text-[#F0ABFC] text-[10px] tracking-[0.3em] hover:bg-[#F0ABFC] hover:text-black transition-colors"
            style={{ boxShadow: "0 0 16px rgba(240,171,252,0.35)" }}
          >
            <Sparkles size={12} /> {t("menu.get_season") || "SEASON PASS"}
          </button>
        )}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-8 md:px-16 py-10 md:py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        {/* Left: Title */}
        <div className="md:col-span-7 flex flex-col justify-between drift-in">
          <div className="flex items-center gap-3 text-[10px] tracking-[0.4em] text-white/60">
            <span className="w-8 h-px bg-cyan-300" />
            <span>{t("menu.banner")}</span>
          </div>

          <div className="mt-16">
            <div className="text-cyan-300 text-xs tracking-[0.4em] mb-4 pulse-glow">{t("menu.protocol")}</div>
            <h1 className="font-display font-black text-6xl md:text-8xl leading-[0.9] tracking-tighter">
              {t("menu.title1")}
              <br />
              <span className="glow-cyan text-cyan-300">{t("menu.title2")}</span>
              <br />
              <span className="text-white/90">{t("menu.title3")}</span>
            </h1>
            <p className="mt-8 max-w-xl text-sm md:text-base text-white/60 leading-relaxed">
              {t("menu.tagline")}
            </p>
          </div>

          <div className="mt-12 flex flex-col gap-4">
            {/* Mode toggle: 2D vs 3D */}
            <div className="flex gap-2 items-center flex-wrap" data-testid="mode-toggle">
              <span className="text-[10px] tracking-[0.4em] text-white/50 mr-2">{t("menu.mode")}</span>
              <button
                data-testid="mode-2d-button"
                onClick={() => { sfx.click(); setMode("2d"); }}
                className="flex items-center gap-2 px-4 py-2 text-xs tracking-[0.3em] transition-colors border"
                style={{
                  borderColor: mode === "2d" ? "#00F0FF" : "rgba(255,255,255,0.15)",
                  color: mode === "2d" ? "#00F0FF" : "rgba(255,255,255,0.55)",
                  boxShadow: mode === "2d" ? "0 0 18px rgba(0,240,255,0.4)" : "none",
                }}
              >
                <Grid3x3 size={14} /> {t("menu.mode_2d")}
              </button>
              <button
                data-testid="mode-3d-button"
                onClick={() => { sfx.click(); setMode("3d"); }}
                className="flex items-center gap-2 px-4 py-2 text-xs tracking-[0.3em] transition-colors border"
                style={{
                  borderColor: mode === "3d" ? "#FFD400" : "rgba(255,255,255,0.15)",
                  color: mode === "3d" ? "#FFD400" : "rgba(255,255,255,0.55)",
                  boxShadow: mode === "3d" ? "0 0 18px rgba(255,212,0,0.4)" : "none",
                }}
              >
                <Box size={14} /> {t("menu.mode_3d")}
                {!isPro && <Lock size={11} />}
              </button>
            </div>

            <SkinLoadoutBar />

            <div className="flex flex-wrap gap-4">
            <button
              data-testid="play-outdoor-button"
              onClick={() => start("outdoor")}
              className="group relative px-8 py-4 border border-cyan-300 text-cyan-300 hover:bg-cyan-300 hover:text-black transition-colors active:scale-95"
              style={{ boxShadow: "0 0 24px rgba(0,240,255,0.35)" }}
            >
              <span className="flex items-center gap-3 text-sm tracking-[0.3em] font-semibold">
                <Zap size={16} /> {t("menu.play_outdoor")}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </span>
              <span className="block text-[10px] tracking-[0.25em] mt-1 opacity-70">{t("menu.outdoor_sub")} · {mode.toUpperCase()}</span>
            </button>

            <button
              data-testid="play-underground-button"
              onClick={() => start("underground")}
              className="group relative px-8 py-4 border border-[#FF0055] text-[#FF0055] hover:bg-[#FF0055] hover:text-black transition-colors active:scale-95"
              style={{ boxShadow: "0 0 24px rgba(255,0,85,0.35)" }}
            >
              <span className="flex items-center gap-3 text-sm tracking-[0.3em] font-semibold">
                <Cpu size={16} /> {t("menu.play_underground")}
                {!isPro && <Lock size={12} className="text-[#FFD400]" />}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </span>
              <span className="block text-[10px] tracking-[0.25em] mt-1 opacity-70">
                {t("menu.underground_sub")} · {mode.toUpperCase()}{!isPro && " · " + (t("menu.unlock_hint") || "$1.99 UNLOCKS EVERYTHING TODAY")}
              </span>
            </button>
            </div>

            {/* Daily Challenge — free for everyone, seeded same for the whole world */}
            <div className="w-full max-w-md">
              <button
                data-testid="play-daily-button"
                onClick={() => start("daily")}
                className="group relative px-8 py-4 border border-[#FFD400] text-[#FFD400] hover:bg-[#FFD400] hover:text-black transition-colors active:scale-95 w-full"
                style={{ boxShadow: "0 0 28px rgba(255,212,0,0.35)" }}
              >
                <span className="flex items-center gap-3 text-sm tracking-[0.3em] font-semibold">
                  <CalendarClock size={16} /> {t("menu.play_daily") || "DAILY CHALLENGE"}
                  {getLifeTokens() > 0 && (
                    <span
                      data-testid="duel-lives-badge"
                      className="ml-auto flex items-center gap-1 px-2 py-0.5 border text-[10px] tracking-[0.2em] border-[#39FF14] text-[#39FF14]"
                      style={{ background: "rgba(0,0,0,0.35)" }}
                      title="Duel-won lives banked · consumed one per run"
                    >
                      +{getLifeTokens()} ♥
                    </span>
                  )}
                  {streakInfo.streak > 0 && (
                    <span
                      data-testid="streak-badge"
                      className={`${getLifeTokens() > 0 ? "" : "ml-auto"} flex items-center gap-1 px-2 py-0.5 border text-[10px] tracking-[0.2em]`}
                      style={{
                        borderColor: streakInfo.atRisk ? "#FF5555" : "#FF9020",
                        color: streakInfo.atRisk ? "#FF5555" : "#FF9020",
                        background: "rgba(0,0,0,0.35)",
                      }}
                    >
                      <Flame size={11} /> {streakInfo.streak}
                    </span>
                  )}
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </span>
                <span className="block text-[10px] tracking-[0.25em] mt-1 opacity-70">
                  {streakInfo.atRisk
                    ? (t("menu.daily_at_risk") || "STREAK AT RISK · PLAY TODAY TO KEEP IT")
                    : (t("menu.daily_sub") || "SAME MAP FOR ALL PLAYERS · FREE · DAILY BOARD")}
                </span>
              </button>
              {reminderSupported() && (
                <button
                  data-testid="reminder-toggle"
                  onClick={async () => {
                    sfx.click();
                    if (reminderOn) {
                      optOutReminder();
                      setReminderOn(false);
                    } else {
                      const res = await optInReminder();
                      setReminderOn(res === "granted");
                    }
                  }}
                  className="mt-2 flex items-center gap-2 text-[10px] tracking-[0.3em] text-white/50 hover:text-[#FFD400] transition-colors"
                >
                  {reminderOn ? <Bell size={12} /> : <BellOff size={12} />}
                  {reminderOn
                    ? (t("menu.reminder_on") || "DAILY REMINDER · ON")
                    : (t("menu.reminder_off") || "REMIND ME EACH DAY")}
                </button>
              )}
              {pushSupported() && (
                <button
                  data-testid="push-toggle"
                  onClick={togglePush}
                  className="mt-2 ml-4 flex items-center gap-2 text-[10px] tracking-[0.3em] text-white/50 hover:text-[#F0ABFC] transition-colors"
                >
                  {pushOn ? <Bell size={12} /> : <BellOff size={12} />}
                  {pushOn
                    ? (t("menu.push_on") || "PUSH ALERTS · ON")
                    : (t("menu.push_off") || "PUSH ALERTS · OFF")}
                </button>
              )}
            </div>

            {/* Rooftop — Pro level with wind mechanic */}
            <button
              data-testid="play-rooftop-button"
              onClick={() => start("rooftop")}
              className="group relative px-8 py-4 border border-[#FF9020] text-[#FF9020] hover:bg-[#FF9020] hover:text-black transition-colors active:scale-95 w-full max-w-md"
              style={{ boxShadow: "0 0 28px rgba(255,144,32,0.35)" }}
            >
              <span className="flex items-center gap-3 text-sm tracking-[0.3em] font-semibold">
                <Wind size={16} /> {t("menu.play_rooftop") || "PLAY · ROOFTOP"}
                {!isPro && <Lock size={12} className="text-[#FFD400]" />}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </span>
              <span className="block text-[10px] tracking-[0.25em] mt-1 opacity-70">
                {t("menu.rooftop_sub") || "TWO EXITS · GUSTY WIND · SUNSET"} · {mode.toUpperCase()}{!isPro && " · " + (t("menu.unlock_hint") || "$1.99 UNLOCKS EVERYTHING TODAY")}
              </span>
            </button>

            {/* Highway — Pro level, lane-based traffic (Frogger-style) */}
            <button
              data-testid="play-highway-button"
              onClick={() => start("highway")}
              className="group relative px-8 py-4 border border-[#B8C4FF] text-[#B8C4FF] hover:bg-[#B8C4FF] hover:text-black transition-colors active:scale-95 w-full max-w-md"
              style={{ boxShadow: "0 0 28px rgba(184,196,255,0.35)" }}
            >
              <span className="flex items-center gap-3 text-sm tracking-[0.3em] font-semibold">
                <Truck size={16} /> {t("menu.play_highway") || "PLAY · HIGHWAY"}
                {!isPro && <Lock size={12} className="text-[#FFD400]" />}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </span>
              <span className="block text-[10px] tracking-[0.25em] mt-1 opacity-70">
                {t("menu.highway_sub") || "6 LANES · CROSS THE TRAFFIC · NIGHT"} · {mode.toUpperCase()}{!isPro && " · " + (t("menu.unlock_hint") || "$1.99 UNLOCKS EVERYTHING TODAY")}
              </span>
            </button>

            {/* Snowstorm — Pro level, slippery physics */}
            <button
              data-testid="play-snowstorm-button"
              onClick={() => start("snowstorm")}
              className="group relative px-8 py-4 border border-[#B8E0FF] text-[#B8E0FF] hover:bg-[#B8E0FF] hover:text-black transition-colors active:scale-95 w-full max-w-md"
              style={{ boxShadow: "0 0 28px rgba(184,224,255,0.35)" }}
            >
              <span className="flex items-center gap-3 text-sm tracking-[0.3em] font-semibold">
                <Snowflake size={16} /> {t("menu.play_snowstorm") || "PLAY · SNOWSTORM"}
                {!isPro && <Lock size={12} className="text-[#FFD400]" />}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </span>
              <span className="block text-[10px] tracking-[0.25em] mt-1 opacity-70">
                {t("menu.snowstorm_sub") || "SLIPPERY ICE · WHITEOUT · 2 EXITS"} · {mode.toUpperCase()}{!isPro && " · " + (t("menu.unlock_hint") || "$1.99 UNLOCKS EVERYTHING TODAY")}
              </span>
            </button>

            {/* Tunnels — Pro level, tight corridor maze */}
            <button
              data-testid="play-tunnels-button"
              onClick={() => start("tunnels")}
              className="group relative px-8 py-4 border border-[#A78BFA] text-[#A78BFA] hover:bg-[#A78BFA] hover:text-black transition-colors active:scale-95 w-full max-w-md"
              style={{ boxShadow: "0 0 28px rgba(167,139,250,0.35)" }}
            >
              <span className="flex items-center gap-3 text-sm tracking-[0.3em] font-semibold">
                <GitBranch size={16} /> {t("menu.play_tunnels") || "PLAY · TUNNELS"}
                {!isPro && <Lock size={12} className="text-[#FFD400]" />}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </span>
              <span className="block text-[10px] tracking-[0.25em] mt-1 opacity-70">
                {t("menu.tunnels_sub") || "NARROW MAZE · 2 EXITS · TIGHT CORNERS"} · {mode.toUpperCase()}{!isPro && " · " + (t("menu.unlock_hint") || "$1.99 UNLOCKS EVERYTHING TODAY")}
              </span>
            </button>

            {/* Beach — Pro level, sun-baked sand + palm obstacles */}
            <button
              data-testid="play-beach-button"
              onClick={() => start("beach")}
              className="group relative px-8 py-4 border border-[#FFD48A] text-[#FFD48A] hover:bg-[#FFD48A] hover:text-black transition-colors active:scale-95 w-full max-w-md"
              style={{ boxShadow: "0 0 28px rgba(255,212,138,0.35)" }}
            >
              <span className="flex items-center gap-3 text-sm tracking-[0.3em] font-semibold">
                <Sun size={16} /> {t("menu.play_beach") || "PLAY · BEACH"}
                {!isPro && <Lock size={12} className="text-[#FFD400]" />}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </span>
              <span className="block text-[10px] tracking-[0.25em] mt-1 opacity-70">
                {t("menu.beach_sub") || "SAND FRICTION · PALM TREES · GOLDEN HOUR"} · {mode.toUpperCase()}{!isPro && " · " + (t("menu.unlock_hint") || "$1.99 UNLOCKS EVERYTHING TODAY")}
              </span>
            </button>

            {/* Neon Rain — Season Pass exclusive bonus level */}
            <button
              data-testid="play-neon-rain-button"
              onClick={() => start("neon_rain")}
              className="group relative px-8 py-4 border border-[#F0ABFC] text-[#F0ABFC] hover:bg-[#F0ABFC] hover:text-black transition-colors active:scale-95 w-full max-w-md"
              style={{ boxShadow: "0 0 28px rgba(240,171,252,0.35)" }}
            >
              <span className="flex items-center gap-3 text-sm tracking-[0.3em] font-semibold">
                <CloudRain size={16} /> {t("menu.play_neon_rain") || "PLAY · NEON RAIN"}
                {!seasonActive && <Sparkles size={12} className="text-[#F0ABFC]" />}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </span>
              <span className="block text-[10px] tracking-[0.25em] mt-1 opacity-70">
                {t("menu.neon_rain_sub") || "WET NEON NIGHT · 2 EXITS · SEASON PASS EXCLUSIVE"} · {mode.toUpperCase()}
              </span>
            </button>
          </div>

          <div className="mt-8 flex gap-4 flex-wrap">
            <button
              data-testid="leaderboard-nav-button"
              onClick={() => { sfx.click(); nav("/leaderboard"); }}
              className="text-xs tracking-[0.3em] flex items-center gap-2 text-white/70 hover:text-cyan-300 transition-colors border-b border-transparent hover:border-cyan-300 pb-1"
            >
              <Trophy size={14} /> {t("menu.leaderboard")}
            </button>
            <button
              data-testid="shop-nav-button"
              onClick={() => { sfx.click(); setShopOpen(true); }}
              className="text-xs tracking-[0.3em] flex items-center gap-2 text-white/70 hover:text-[#FFD400] transition-colors border-b border-transparent hover:border-[#FFD400] pb-1"
            >
              <Coins size={14} className="text-[#FFD400]" /> {t("menu.shop") || "SHOP"} <span className="text-[#FFD400] font-bold">{chipBalance}</span>
            </button>
            <button
              data-testid="achievements-nav-button"
              onClick={() => { sfx.click(); setAchievementsOpen(true); }}
              className="text-xs tracking-[0.3em] flex items-center gap-2 text-white/70 hover:text-[#FFD400] transition-colors border-b border-transparent hover:border-[#FFD400] pb-1"
            >
              <Award size={14} /> {t("menu.achievements") || "ACHIEVEMENTS"} <span className="text-white/40">{achievementCount}/{ACHIEVEMENTS.length}</span>
            </button>
            {seasonActive && (
              <button
                data-testid="season-track-nav-button"
                onClick={() => { sfx.click(); setTrackOpen(true); }}
                className="text-xs tracking-[0.3em] flex items-center gap-2 text-white/70 hover:text-[#F0ABFC] transition-colors border-b border-transparent hover:border-[#F0ABFC] pb-1"
              >
                <Sparkles size={14} /> {t("menu.season_track") || "SEASON TRACK"} <span className="text-white/40">DAY {trackDay || 1}</span>
              </button>
            )}
            {seasonActive && (
              <button
                data-testid="weather-picker-button"
                onClick={() => {
                  sfx.click();
                  const order = ["off", "rain", "fog", "wind"];
                  const next = order[(order.indexOf(weather.type) + 1) % order.length];
                  const w = setWeather({ type: next, intensity: weather.intensity });
                  setWeatherState(w);
                  toast.success(`${t("menu.weather_toast") || "WEATHER"} · ${WEATHER_LABELS[w.type]}`);
                }}
                className="text-xs tracking-[0.3em] flex items-center gap-2 text-white/70 hover:text-[#B8E0FF] transition-colors border-b border-transparent hover:border-[#B8E0FF] pb-1"
              >
                {weather.type === "rain" ? <CloudRain size={14} /> :
                 weather.type === "fog" ? <CloudFog size={14} /> :
                 weather.type === "wind" ? <Wind size={14} /> :
                 <Sun size={14} />}
                {t("menu.weather") || "WEATHER"} <span className="text-white/40">{WEATHER_LABELS[weather.type]}</span>
              </button>
            )}
            <button
              data-testid="roulette-button"
              disabled={!rouletteReady}
              onClick={() => {
                if (!rouletteReady) return;
                sfx.click();
                const streak = getRouletteStreak();
                if (streak === 2 && !isRoulette5xPending()) {
                  // Jackpot Forecast — 2 wins into the streak, one from 5×
                  toast.success("1 WIN FROM JACKPOT · win this run to arm 5×");
                }
                const ticket = spinRoulette();
                setRouletteReady(false);
                const jack = isRoulette5xPending();
                toast.success(`ROULETTE SPUN · ${ticket.type.toUpperCase()} · ${jack ? "5× JACKPOT" : "3× ON WIN"}`);
                nav("/play/outdoor");
              }}
              className={`text-xs tracking-[0.3em] flex items-center gap-2 border-b border-transparent pb-1 ${rouletteReady ? (isRoulette5xPending() ? "text-[#FFD400] hover:border-[#FFD400]" : "text-[#F0ABFC] hover:border-[#F0ABFC]") : "text-white/30"}`}
            >
              <Dice5 size={14} /> {t("menu.roulette") || "ROULETTE"} <span className="text-white/40">{rouletteReady ? (isRoulette5xPending() ? "5× JACKPOT" : "1 SPIN · 3×") : "SPUN"}</span>
            </button>
            <button
              data-testid="handle-open-button"
              onClick={() => { sfx.click(); setHandleOpen(true); }}
              className="text-xs tracking-[0.3em] flex items-center gap-2 text-white/70 hover:text-[#00F0FF] transition-colors border-b border-transparent hover:border-[#00F0FF] pb-1"
            >
              <UserCircle2 size={14} /> {t("menu.handle") || "HANDLE"} <span className="text-white/40">{publicHandle || "ANON"}</span>
            </button>
            <button
              data-testid="trade-open-button"
              onClick={() => { sfx.click(); setTradeOpen(true); }}
              className="text-xs tracking-[0.3em] flex items-center gap-2 text-white/70 hover:text-[#F0ABFC] transition-colors border-b border-transparent hover:border-[#F0ABFC] pb-1"
            >
              <Sparkles size={14} /> TRADE HALL <span className="text-white/40">GEMS</span>
            </button>
            <button
              data-testid="clan-open-button"
              onClick={() => { sfx.click(); setClanOpen(true); }}
              className="text-xs tracking-[0.3em] flex items-center gap-2 text-white/70 hover:text-[#39FF14] transition-colors border-b border-transparent hover:border-[#39FF14] pb-1"
            >
              <Trophy size={14} /> CLAN <span className="text-white/40">{cachedClan ? `[${cachedClan.tag}]` : "SOLO"}</span>
            </button>
            <button
              data-testid="howto-open-button"
              onClick={() => { sfx.click(); setHowToOpen(true); }}
              className="text-xs tracking-[0.3em] flex items-center gap-2 text-white/70 hover:text-cyan-300 transition-colors border-b border-transparent hover:border-cyan-300 pb-1"
            >
              <HelpCircle size={14} /> {t("menu.how_to_play")}
            </button>
          </div>
        </div>

        {/* Right: Stats */}
        <div className="md:col-span-5 flex flex-col gap-6 drift-in">
          <VaultFrenzyBanner />
          <AuctionWatchReminder onOpenTrade={() => setTradeOpen(true)} />
          <SnipeCounterCard />
          <LoyaltyTipsBanner />
          <OutbidRefundsBanner />
          <HandleSpotlightCard />
          <GemHallOfFameStrip onFlip={() => setTradeOpen(true)} />
          <JackpotVaultCard />
          <DailyRecapCard />
          <div className="border border-white/10 p-6 bg-black/40 backdrop-blur-md">
            <div className="text-[10px] tracking-[0.4em] text-white/50 mb-4">{t("menu.local_records")}</div>
            <div className="flex flex-col gap-4">
              <StatRow label={t("menu.outdoor")} value={highs.outdoor} accent="#00F0FF" testid="high-outdoor" />
              <StatRow label={t("menu.underground")} value={highs.underground} accent="#FF0055" testid="high-underground" />
            </div>
          </div>

          <WagerLeaderboardCard />
          <BuggyLeaderboardCard />
          <ClanLeaderboardCard onOpenClan={() => setClanOpen(true)} />

          <div className="border border-white/10 p-6 bg-black/40 backdrop-blur-md">
            <div className="text-[10px] tracking-[0.4em] text-white/50 mb-4">{t("menu.power_ups")}</div>
            <ul className="text-xs space-y-3 text-white/80">
              <li className="flex gap-3 items-start">
                <span className="w-6 h-6 flex items-center justify-center border border-[#39FF14] text-[#39FF14] text-[10px] font-bold shrink-0">S</span>
                <span><b className="text-[#39FF14]">{t("powerups.shield")}</b> · {t("powerups.shield_desc")}</span>
              </li>
              <li className="flex gap-3 items-start">
                <span className="w-6 h-6 flex items-center justify-center border border-cyan-300 text-cyan-300 text-[10px] font-bold shrink-0">»</span>
                <span><b className="text-cyan-300">{t("powerups.boost")}</b> · {t("powerups.boost_desc")}</span>
              </li>
              <li className="flex gap-3 items-start">
                <span className="w-6 h-6 flex items-center justify-center border border-[#FFD400] text-[#FFD400] text-[10px] font-bold shrink-0">F</span>
                <span><b className="text-[#FFD400]">{t("powerups.freeze")}</b> · {t("powerups.freeze_desc")}</span>
              </li>
            </ul>
          </div>

          <div className="border border-white/10 p-6 bg-black/40 backdrop-blur-md">
            <div className="text-[10px] tracking-[0.4em] text-white/50 mb-3">{t("menu.controls")}</div>
            <div className="grid grid-cols-2 gap-2 text-xs text-white/70">
              <div>{t("menu.move")}</div>
              <div className="text-cyan-300">{t("menu.keys_move")}</div>
              <div>{t("menu.pause")}</div>
              <div className="text-cyan-300">{t("menu.key_pause")}</div>
              <div>{t("menu.objective")}</div>
              <div className="text-[#39FF14]">{t("menu.reach_exit")}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-4 left-0 right-0 text-center text-[10px] tracking-[0.4em] text-white/30 z-20 pointer-events-auto">
        {t("menu.footer")}
        <span className="mx-3 text-white/20">·</span>
        <Link
          to="/trade-highlights"
          data-testid="trade-highlights-link"
          className="text-white/40 hover:text-[#F0ABFC] transition-colors"
        >
          TRADE HALL HIGHLIGHTS →
        </Link>
      </div>

      <HowToPlayDialog open={howToOpen} onOpenChange={setHowToOpen} />
      <PaywallDialog
        open={paywall.open}
        onOpenChange={(v) => setPaywall((p) => ({ ...p, open: v }))}
        feature={paywall.feature}
        defaultTab={paywall.defaultTab}
        onUnlocked={() => { setPro(true); setSeasonActive(getIsSeason()); setSeasonExpires(getSeasonExpiresAt()); setSeasonPlan(getPlan()); }}
      />
      <SkinPicker
        open={skinOpen}
        onOpenChange={setSkinOpen}
        onSelect={(s) => setSkin(s)}
      />
      <AchievementsDialog open={achievementsOpen} onOpenChange={setAchievementsOpen} />
      <SeasonTrackDialog open={trackOpen} onOpenChange={setTrackOpen} />
      <ShopDialog open={shopOpen} onOpenChange={(v) => { setShopOpen(v); setChipBalance(getChips()); }} />
      <HandleDialog
        open={handleOpen}
        onOpenChange={setHandleOpen}
        onSaved={(h) => setPublicHandleState(h)}
      />
      <TradeHallDialog open={tradeOpen} onOpenChange={setTradeOpen} />
      <ClanDialog
        open={clanOpen}
        onOpenChange={setClanOpen}
        onChanged={() => setCachedClanState(getCachedClan())}
      />
      {resumeRun && (
        <div
          data-testid="resume-run-banner"
          className="fixed bottom-6 right-6 z-40 border border-[#F0ABFC] bg-black/85 backdrop-blur-xl p-4 max-w-sm"
          style={{ boxShadow: "0 0 22px rgba(240,171,252,0.35)" }}
        >
          <div className="text-[10px] tracking-[0.4em] text-[#F0ABFC] mb-1">RUN IN PROGRESS · ANOTHER DEVICE</div>
          <div className="text-xs text-white/80 tracking-wider">
            Level <span className="font-mono text-white">{resumeRun.level}</span>
            {typeof resumeRun.score === "number" && <> · <span className="font-mono text-white">{String(resumeRun.score).padStart(6, "0")}</span> pts</>}
            {typeof resumeRun.stage === "number" && <> · stage {resumeRun.stage}</>}
          </div>
          <div className="flex gap-2 mt-3">
            <button
              data-testid="resume-run-jump"
              onClick={() => { sfx.click(); nav(`/play/${resumeRun.level}`); }}
              className="px-3 py-1.5 border border-[#F0ABFC] text-[#F0ABFC] hover:bg-[#F0ABFC] hover:text-black transition-colors text-[10px] tracking-[0.3em]"
            >
              JUMP BACK IN →
            </button>
            <button
              data-testid="resume-run-dismiss"
              onClick={() => setResumeRun(null)}
              className="px-3 py-1.5 border border-white/20 text-white/60 hover:border-white/50 hover:text-white text-[10px] tracking-[0.3em]"
            >
              DISMISS
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StatRow({ label, value, accent, testid }) {
  return (
    <div className="flex items-center justify-between border-b border-white/5 pb-3" data-testid={testid}>
      <div className="text-xs tracking-[0.3em] text-white/60">{label}</div>
      <div className="text-2xl font-bold tabular-nums" style={{ color: accent, textShadow: `0 0 12px ${accent}80` }}>
        {String(value).padStart(6, "0")}
      </div>
    </div>
  );
}

const GEM_COLORS = { crown: "#FFD400", fire: "#FF6030", star: "#F0ABFC", prismatic: "#B0F0FF" };

function WagerLeaderboardCard() {
  const [rows, setRows] = React.useState(null);
  const [weekStart, setWeekStart] = React.useState("");
  React.useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/wager/leaderboard?limit=10`)
      .then((res) => {
        setRows(res.data?.rows || []);
        setWeekStart(res.data?.week_start || "");
      })
      .catch(() => setRows([]));
  }, []);
  if (rows === null) return null;
  return (
    <div className="border border-white/10 p-6 bg-black/40 backdrop-blur-md" data-testid="wager-leaderboard-card">
      <div className="text-[10px] tracking-[0.4em] text-[#FFD400] mb-4 flex items-center gap-2">
        <Coins size={12} className="text-[#FFD400]" /> WAGER LEADERBOARD · {weekStart}
      </div>
      {rows.length === 0 ? (
        <div className="text-[10px] tracking-[0.3em] text-white/40 text-center py-4">NO WAGERS THIS WEEK · BE THE FIRST</div>
      ) : (
        <ol className="space-y-2">
          {rows.map((r, i) => (
            <li
              key={r.handle + i}
              className={`flex items-center justify-between text-xs font-mono border-b border-white/5 pb-2 ${r.gem === "prismatic" ? "prismatic-row" : "gem-row"}`}
              data-testid={`wager-row-${i}`}
              style={r.gem ? {
                paddingLeft: 6,
                marginLeft: -6,
                borderLeft: `2px solid ${GEM_COLORS[r.gem] || "#FFD400"}`,
                boxShadow: `inset 8px 0 20px -8px ${GEM_COLORS[r.gem] || "#FFD400"}66`,
              } : {}}
            >
              <div className="flex items-center gap-3">
                <span className={`w-6 text-right ${i === 0 ? "text-[#FFD400]" : i === 1 ? "text-white/70" : i === 2 ? "text-[#CD7F32]" : "text-white/40"} tabular-nums`}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span
                  className={`text-white/80 ${r.gem === "prismatic" ? "prismatic-text" : ""}`}
                  style={r.gem && r.gem !== "prismatic" ? { color: GEM_COLORS[r.gem], textShadow: `0 0 8px ${GEM_COLORS[r.gem]}88` } : {}}
                >
                  {r.handle}
                </span>
                {r.via_trade && (
                  <span
                    data-testid={`wager-traded-badge-${i}`}
                    className="text-[8px] tracking-[0.3em] px-1 py-[1px] border border-[#F0ABFC]/50 text-[#F0ABFC] bg-[#F0ABFC]/10"
                    title="Gem acquired via Trade Hall"
                  >
                    TRADED
                  </span>
                )}
                {r.duelist && (
                  <span
                    data-testid={`wager-duelist-badge-${i}`}
                    className="prismatic-text text-[8px] tracking-[0.3em] px-1 py-[1px] border prismatic-row"
                    style={{ borderWidth: 1 }}
                    title="3+ clean duel wins in a row"
                  >
                    DUELIST
                  </span>
                )}
                {r.gavel && (
                  <span
                    data-testid={`wager-gavel-badge-${i}`}
                    className="text-[8px] tracking-[0.3em] px-1 py-[1px] border border-[#FFD400] text-[#FFD400] bg-[#FFD400]/10"
                    title="3+ auction wins this week"
                    style={{ textShadow: "0 0 6px rgba(255,212,0,0.8)" }}
                  >
                    GAVEL
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4">
                <span className="text-white/40 text-[10px] tabular-nums">{r.wins}/{r.runs}</span>
                <span
                  className={`tabular-nums font-bold ${r.net_chips >= 0 ? "text-[#39FF14]" : "text-[#FF0055]"}`}
                  style={{ textShadow: r.net_chips >= 0 ? "0 0 10px rgba(57,255,20,0.5)" : "0 0 10px rgba(255,0,85,0.5)" }}
                >
                  {r.net_chips >= 0 ? "+" : ""}
                  {r.net_chips}
                </span>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function DailyRecapCard() {
  const recap = React.useMemo(() => getYesterdayRouletteRecap(), []);
  if (!recap) return null;
  const wonColor = recap.won ? "#39FF14" : "#FF0055";
  const wonGlow = recap.won ? "rgba(57,255,20,0.55)" : "rgba(255,0,85,0.55)";
  const shareRecap = async () => {
    sfx.click();
    const shareText =
      `🎰 Yesterday's roulette · ${recap.weather?.toUpperCase() || "?"}\n` +
      `Score ${String(recap.score || 0).padStart(6, "0")}${recap.won ? ` · +${recap.chips || 0} chips` : ""}${recap.jackpot ? " · 5× JACKPOT" : ""}\n` +
      `${recap.streak >= 2 ? `🔥 ${recap.streak} in a row — ` : ""}Spin yours: ${window.location.origin}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Parking Plot Roulette", text: shareText });
        toast.success("Shared!");
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(shareText);
        toast.success("Copied to clipboard");
      } else {
        toast.info("Sharing not supported on this device");
      }
    } catch {}
  };
  return (
    <div
      className="border p-4 bg-black/40 backdrop-blur-md drift-in"
      data-testid="daily-recap-card"
      style={{ borderColor: wonColor, boxShadow: `0 0 20px ${wonGlow}` }}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] tracking-[0.4em]" style={{ color: wonColor }}>
          YESTERDAY · ROULETTE {recap.won ? "WON" : "LOST"}
        </div>
        {recap.won && (
          <button
            data-testid="recap-share-button"
            onClick={shareRecap}
            className="text-[10px] tracking-[0.3em] px-2 py-0.5 border transition-colors"
            style={{ borderColor: wonColor, color: wonColor }}
          >
            SHARE
          </button>
        )}
      </div>
      <div className="text-xs text-white/80 tracking-wider">
        {recap.weather?.toUpperCase() || "?"} · score {String(recap.score || 0).padStart(6, "0")}
        {recap.won && recap.chips ? ` · +${recap.chips} chips` : ""}
        {recap.jackpot ? " · 5× JACKPOT" : ""}
      </div>
      <div className="text-[10px] tracking-[0.3em] text-white/50 mt-1">
        {recap.won ? (recap.streak >= 2 ? `${recap.streak} in a row — spin today for the jackpot` : "Great run — spin again today") : "Bounce back — today's spin is fresh"}
      </div>
    </div>
  );
}

function BuggyLeaderboardCard() {  const [rows, setRows] = React.useState(null);
  const [weekStart, setWeekStart] = React.useState("");
  React.useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/buggy/leaderboard?limit=10`)
      .then((res) => {
        setRows(res.data?.rows || []);
        setWeekStart(res.data?.week_start || "");
      })
      .catch(() => setRows([]));
  }, []);
  if (rows === null || rows.length === 0) return null;
  return (
    <div className="border border-white/10 p-6 bg-black/40 backdrop-blur-md" data-testid="buggy-leaderboard-card">
      <div className="text-[10px] tracking-[0.4em] text-[#FF8A00] mb-4 flex items-center gap-2">
        <Flame size={12} className="text-[#FF8A00]" /> BUGGY SURVIVORS · {weekStart}
      </div>
      <ol className="space-y-2">
        {rows.map((r, i) => (
          <li
            key={r.handle + i}
            className={`flex items-center justify-between text-xs font-mono border-b border-white/5 pb-2 ${r.gem === "prismatic" ? "prismatic-row" : "gem-row"}`}
            data-testid={`buggy-row-${i}`}
            style={r.gem ? {
              paddingLeft: 6,
              marginLeft: -6,
              borderLeft: `2px solid ${GEM_COLORS[r.gem] || "#FFD400"}`,
              boxShadow: `inset 8px 0 20px -8px ${GEM_COLORS[r.gem] || "#FFD400"}66`,
            } : {}}
          >
            <div className="flex items-center gap-3">
              <span className={`w-6 text-right ${i === 0 ? "text-[#FF8A00]" : "text-white/50"} tabular-nums`}>
                {String(i + 1).padStart(2, "0")}
              </span>
              <span
                className={`text-white/80 ${r.gem === "prismatic" ? "prismatic-text" : ""}`}
                style={r.gem && r.gem !== "prismatic" ? { color: GEM_COLORS[r.gem], textShadow: `0 0 8px ${GEM_COLORS[r.gem]}88` } : {}}
              >
                {r.handle}
              </span>
              {r.via_trade && (
                <span
                  data-testid={`buggy-traded-badge-${i}`}
                  className="text-[8px] tracking-[0.3em] px-1 py-[1px] border border-[#F0ABFC]/50 text-[#F0ABFC] bg-[#F0ABFC]/10"
                  title="Gem acquired via Trade Hall"
                >
                  TRADED
                </span>
              )}
              {r.duelist && (
                <span
                  data-testid={`buggy-duelist-badge-${i}`}
                  className="prismatic-text text-[8px] tracking-[0.3em] px-1 py-[1px] border prismatic-row"
                  style={{ borderWidth: 1 }}
                  title="3+ clean duel wins in a row"
                >
                  DUELIST
                </span>
              )}
              {r.gavel && (
                <span
                  data-testid={`buggy-gavel-badge-${i}`}
                  className="text-[8px] tracking-[0.3em] px-1 py-[1px] border border-[#FFD400] text-[#FFD400] bg-[#FFD400]/10"
                  title="3+ auction wins this week"
                  style={{ textShadow: "0 0 6px rgba(255,212,0,0.8)" }}
                >
                  GAVEL
                </span>
              )}
            </div>
            <span
              className="tabular-nums font-bold text-[#FF8A00]"
              style={{ textShadow: "0 0 10px rgba(255,138,0,0.55)" }}
            >
              {r.survivals} ×
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function ClanLeaderboardCard({ onOpenClan }) {
  const [rows, setRows] = React.useState(null);
  const [weekStart, setWeekStart] = React.useState("");
  React.useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/clan/leaderboard?limit=8`)
      .then((res) => {
        setRows(res.data?.rows || []);
        setWeekStart(res.data?.week_start || "");
      })
      .catch(() => setRows([]));
  }, []);
  if (rows === null) return null;
  return (
    <div
      className="border border-white/10 p-6 bg-black/40 backdrop-blur-md"
      data-testid="clan-leaderboard-card"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] tracking-[0.4em] text-[#39FF14] flex items-center gap-2">
          <Trophy size={12} className="text-[#39FF14]" /> CLAN WARS · {weekStart}
        </div>
        {onOpenClan && (
          <button
            data-testid="clan-lb-open-button"
            onClick={onOpenClan}
            className="text-[9px] tracking-[0.3em] text-white/50 hover:text-[#39FF14] border-b border-transparent hover:border-[#39FF14]"
          >
            OPEN →
          </button>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="text-[10px] tracking-[0.3em] text-white/40 text-center py-4">
          NO CLAN CHIPS YET · FOUND ONE
        </div>
      ) : (
        <ol className="space-y-2">
          {rows.map((r, i) => (
            <li
              key={r.clan_id}
              data-testid={`clan-lb-row-${i}`}
              className="flex items-center justify-between text-xs font-mono border-b border-white/5 pb-2"
            >
              <div className="flex items-center gap-3 min-w-0">
                <span
                  className={`w-6 text-right ${
                    i === 0 ? "text-[#FFD400]" : i === 1 ? "text-white/70" : i === 2 ? "text-[#CD7F32]" : "text-white/40"
                  } tabular-nums`}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-[9px] tracking-[0.3em] px-1.5 py-0.5 border border-[#39FF14]/40 text-[#39FF14]/90 bg-[#39FF14]/5">
                  [{r.tag}]
                </span>
                <span className="text-white/80 tracking-wider truncate">{r.name}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-white/40 text-[10px] tabular-nums">{r.contributors}/{r.member_count}</span>
                <span
                  className="tabular-nums font-bold text-[#39FF14]"
                  style={{ textShadow: "0 0 10px rgba(57,255,20,0.5)" }}
                >
                  {r.chips}
                </span>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// ============================================================
// JACKPOT VAULT CARD — 24h lock with +50% bonus if untouched
// ============================================================
function JackpotVaultCard() {
  const [vault, setVaultState] = React.useState(() => getVault());
  const [remaining, setRemaining] = React.useState(() => msUntilUnlock());
  const [claiming, setClaiming] = React.useState(false);

  React.useEffect(() => {
    if (!vault) return;
    const id = setInterval(() => {
      setRemaining(msUntilUnlock());
      setVaultState(getVault());
    }, 1000);
    return () => clearInterval(id);
  }, [vault]);

  if (!vault) return null;

  const unlocked = isVaultUnlocked();
  const mult = currentBonusMult();
  const bonusChips = Math.floor(vault.chips * (mult - 1));
  const frenzyActive = isVaultFrenzy();

  const onClaimEarly = () => {
    if (claiming) return;
    setClaiming(true);
    sfx.click();
    const paid = claimEarly();
    toast.success(`CLAIMED · +${paid} chips (no bonus)`);
    setVaultState(null);
  };
  const onClaimFull = () => {
    if (claiming) return;
    setClaiming(true);
    sfx.click();
    const { paid, bonus, mult: paidMult } = claimUnlocked();
    const pct = Math.round((paidMult - 1) * 100);
    toast.success(`VAULT UNLOCKED · +${paid} chips (+${bonus} bonus @ ${pct}%)`);
    setVaultState(null);
  };

  const glow = unlocked ? "#39FF14" : "#FFD400";
  const glowRgba = unlocked ? "rgba(57,255,20,0.55)" : "rgba(255,212,0,0.55)";

  return (
    <div
      className="border p-4 bg-black/40 backdrop-blur-md drift-in"
      data-testid="jackpot-vault-card"
      style={{ borderColor: glow, boxShadow: `0 0 22px ${glowRgba}` }}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] tracking-[0.4em]" style={{ color: glow }}>
          {unlocked ? "JACKPOT VAULT · READY" : "JACKPOT VAULT · LOCKED"}
        </div>
        <div className="text-[10px] tracking-[0.3em] tabular-nums text-white/80" data-testid="vault-countdown">
          {unlocked ? "READY" : formatLockRemaining(remaining)}
        </div>
      </div>
      <div className="text-xs text-white/80 tracking-wider">
        <span className="tabular-nums font-bold" style={{ color: glow, textShadow: `0 0 10px ${glowRgba}` }}>
          {vault.chips}
        </span>{" "}
        chips {unlocked ? "waiting" : "in vault"}
        {!unlocked && (
          <span className="text-white/50">
            {" "}· +{bonusChips} bonus if untouched
            {frenzyActive && <span className="text-[#FF6EC7]"> · 2× FRENZY!</span>}
          </span>
        )}
      </div>
      <div className="flex gap-2 mt-3">
        {unlocked ? (
          <>
            <button
              data-testid="vault-claim-full-button"
              onClick={onClaimFull}
              className="text-[10px] tracking-[0.3em] px-3 py-1 border transition-colors font-bold"
              style={{ borderColor: glow, color: glow, background: `${glow}11` }}
            >
              CLAIM +{bonusChips} BONUS
            </button>
            {frenzyActive && (
              <button
                data-testid="vault-relock-frenzy-button"
                onClick={() => {
                  const r = relockVaultFrenzy(100, 0.25);
                  if (r.ok) {
                    sfx.click();
                    if (r.ladderUnlocked) {
                      toast.success(`◈ VAULT LADDER UNLOCKED · +5% BASE VAULT BONUS FOREVER`, { duration: 6500 });
                    } else {
                      toast.success(`VAULT RE-LOCKED · +25% extra bonus stacked · 12h`);
                    }
                    setVaultState(getVault());
                  } else {
                    toast.error(String(r.error || "RELOCK FAILED").toUpperCase());
                  }
                }}
                className="text-[10px] tracking-[0.3em] px-3 py-1 border transition-colors font-bold border-[#FF6EC7] text-[#FF6EC7] hover:bg-[#FF6EC7]/10"
                title="Pay 100 chips to lock again for 12h at +25% extra bonus"
              >
                RE-LOCK · 100 CHIPS · +25%
              </button>
            )}
          </>
        ) : (
          <button
            data-testid="vault-claim-early-button"
            onClick={onClaimEarly}
            className="text-[10px] tracking-[0.3em] px-3 py-1 border transition-colors text-white/70 border-white/30 hover:border-white/60 hover:text-white"
          >
            CLAIM EARLY · NO BONUS
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================
// HANDLE-OF-THE-WEEK SPOTLIGHT — top wager earner's handle
// ============================================================
function HandleSpotlightCard() {
  const nav = useNavigate();
  const [spotlight, setSpotlight] = React.useState(null); // { handle, gem, net_chips, wins, runs }
  React.useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/wager/spotlight`)
      .then((res) => setSpotlight(res.data?.spotlight || null))
      .catch(() => setSpotlight(null));
  }, []);
  if (!spotlight || !spotlight.handle) return null;

  const gemColor = GEM_COLORS[spotlight.gem] || "#00F0FF";
  const isPrismatic = spotlight.gem === "prismatic";

  const challenge = () => {
    sfx.click();
    localStorage.setItem("ppe_challenge_target_v1", JSON.stringify({
      handle: spotlight.handle,
      queuedAt: Date.now(),
    }));
    toast.success(`CHALLENGE QUEUED · Race ${spotlight.handle}'s ghost in today's daily`);
    nav("/play/daily");
  };

  return (
    <div
      className="border p-4 bg-black/40 backdrop-blur-md drift-in relative overflow-hidden"
      data-testid="handle-spotlight-card"
      style={{ borderColor: isPrismatic ? "#B0F0FF" : gemColor, boxShadow: `0 0 22px ${isPrismatic ? "rgba(176,240,255,0.5)" : gemColor + "88"}` }}
    >
      <div className="flex items-center justify-between mb-2">
        <div
          className="text-[10px] tracking-[0.4em]"
          style={{ color: isPrismatic ? "#B0F0FF" : gemColor }}
        >
          HANDLE OF THE WEEK
        </div>
        <Crown size={14} style={{ color: isPrismatic ? "#B0F0FF" : gemColor }} />
      </div>
      <div
        className={`text-xl font-bold tracking-wide mb-1 ${isPrismatic ? "prismatic-text" : ""}`}
        style={!isPrismatic ? { color: gemColor, textShadow: `0 0 12px ${gemColor}aa` } : {}}
        data-testid="spotlight-handle"
      >
        {spotlight.handle}
      </div>
      <div className="text-[11px] tracking-wider text-white/60 mb-3 tabular-nums">
        +{spotlight.net_chips} net chips · {spotlight.wins}/{spotlight.runs} wins this week
      </div>
      <button
        data-testid="spotlight-challenge-button"
        onClick={challenge}
        className="text-[10px] tracking-[0.3em] px-3 py-1 border font-bold transition-colors"
        style={{
          borderColor: isPrismatic ? "#B0F0FF" : gemColor,
          color: isPrismatic ? "#B0F0FF" : gemColor,
          background: `${isPrismatic ? "#B0F0FF" : gemColor}11`,
        }}
      >
        CHALLENGE THEM →
      </button>
    </div>
  );
}



// ============================================================
// VAULT FRENZY WEEKEND BANNER — Sat/Sun UTC boosts vault bonus 1.5×→2×
// ============================================================
function VaultFrenzyBanner() {
  const [status, setStatus] = React.useState(() => frenzyStatus());
  React.useEffect(() => {
    const id = setInterval(() => setStatus(frenzyStatus()), 30000);
    return () => clearInterval(id);
  }, []);

  const fmt = (ms) => {
    const totalSec = Math.max(0, Math.floor(ms / 1000));
    const d = Math.floor(totalSec / 86400);
    const h = Math.floor((totalSec % 86400) / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  if (!status.active) {
    if (status.msRemaining > 48 * 60 * 60 * 1000) return null;
    return (
      <div
        className="border border-[#FF6EC7]/30 p-3 bg-[#FF6EC7]/5 backdrop-blur-md drift-in"
        data-testid="vault-frenzy-upcoming"
      >
        <div className="text-[10px] tracking-[0.4em] text-[#FF6EC7]/80 mb-1">
          VAULT FRENZY · INCOMING
        </div>
        <div className="text-xs text-white/70 tracking-wider">
          Bonus doubles to <span className="text-[#FF6EC7] font-bold">+100%</span> in{" "}
          <span className="tabular-nums text-white/90">{fmt(status.msRemaining)}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="border border-[#FF6EC7] p-3 backdrop-blur-md drift-in relative overflow-hidden"
      style={{
        background: "linear-gradient(90deg, rgba(255,110,199,0.10), rgba(240,171,252,0.10), rgba(0,240,255,0.10))",
        boxShadow: "0 0 24px rgba(255,110,199,0.5)",
      }}
      data-testid="vault-frenzy-banner"
    >
      <div className="text-[10px] tracking-[0.4em] text-[#FF6EC7] mb-1 font-bold">
        VAULT FRENZY WEEKEND · ACTIVE
      </div>
      <div className="text-xs text-white/90 tracking-wider">
        Jackpot Vault bonus is{" "}
        <span className="text-[#FF6EC7] font-bold text-sm">+100%</span> — ends in{" "}
        <span className="tabular-nums text-white">{fmt(status.msRemaining)}</span>
      </div>
    </div>
  );
}

// ============================================================
// GEM HALL OF FAME — last 5 completed trades, FLIP if you own it
// ============================================================
function GemHallOfFameStrip({ onFlip }) {
  const [trades, setTrades] = React.useState(null);
  React.useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/gems/hall-of-fame?limit=5`)
      .then((r) => setTrades(r.data?.trades || []))
      .catch(() => setTrades([]));
  }, []);
  if (!trades || trades.length === 0) return null;

  const myHandle = (localStorage.getItem("ppe_public_handle_v1") || "").trim().toLowerCase();
  const gemsMeta = { crown: "♛", fire: "▲", star: "★", prismatic: "◈" };

  return (
    <div
      className="border border-white/10 p-4 bg-black/40 backdrop-blur-md drift-in"
      data-testid="gem-hall-of-fame"
    >
      <div className="text-[10px] tracking-[0.4em] text-white/60 mb-3">GEM HALL OF FAME · LAST 5</div>
      <ol className="space-y-2">
        {trades.map((t, i) => {
          // Strip the gem prefix (♛ / ▲ / etc.) before compare for FLIP eligibility
          const buyerName = String(t.buyer_handle || "").replace(/^\S+\s+/, "").toLowerCase();
          const isMine = myHandle && buyerName === myHandle;
          const gemColor = GEM_COLORS[t.gem] || "#F0ABFC";
          const flipPrice = Math.ceil(t.price * 1.1);
          return (
            <li
              key={i}
              className="flex items-center justify-between text-xs font-mono"
              data-testid={`gem-hof-row-${i}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span style={{ color: gemColor, textShadow: `0 0 6px ${gemColor}66` }}>
                  {gemsMeta[t.gem] || "•"}
                </span>
                <span className="text-white/80 truncate">{t.buyer_handle}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-white/50 text-[10px] tabular-nums">{t.price}</span>
                {isMine ? (
                  <button
                    data-testid={`gem-hof-flip-${i}`}
                    onClick={() => { sfx.click(); localStorage.setItem("ppe_flip_hint_v1", JSON.stringify({ gem: t.gem, price: flipPrice })); if (onFlip) onFlip(); }}
                    className="text-[9px] tracking-[0.3em] px-2 py-[2px] border border-[#F0ABFC] text-[#F0ABFC] hover:bg-[#F0ABFC]/10 transition"
                  >
                    FLIP +{flipPrice}
                  </button>
                ) : (
                  <span className="text-[9px] tracking-[0.3em] text-white/30">SOLD</span>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}


// ============================================================
// LOYALTY TIPS BANNER — chips owed back to sellers whose gems were
// bought at a prismatic discount. Auto-fetched on menu mount.
// ============================================================
function LoyaltyTipsBanner() {
  const [total, setTotal] = React.useState(0);
  const [tips, setTips] = React.useState([]);
  const [claiming, setClaiming] = React.useState(false);

  React.useEffect(() => {
    const email = getMenuEmail();
    if (!email) return;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/gems/tips-pending?email=${encodeURIComponent(email)}`)
      .then((r) => { setTotal(r.data?.total || 0); setTips(r.data?.tips || []); })
      .catch(() => {});
  }, []);

  const claim = async () => {
    const email = getMenuEmail();
    if (!email || claiming || total <= 0) return;
    setClaiming(true);
    sfx.click();
    try {
      const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/gems/tips-claim`, { seller_email: email });
      const paid = r.data?.claimed || 0;
      const streak = r.data?.streak || 0;
      const boostArmed = !!r.data?.boost_armed;
      if (paid > 0) {
        addChipsMenu(paid);
        if (boostArmed) {
          toast.success(`◆ LOYALTY STREAK ×${streak} · +${paid} banked · NEXT SALE +5% BOOST`, { duration: 6500 });
        } else {
          toast.success(`LOYALTY TIP · +${paid} chips banked · ${streak} sale${streak > 1 ? "s" : ""}`);
        }
      }
      setTotal(0);
      setTips([]);
    } catch {
      toast.error("CLAIM FAILED");
    } finally {
      setClaiming(false);
    }
  };

  if (total <= 0) return null;

  return (
    <div
      className="border border-[#39FF14]/50 p-3 bg-[#39FF14]/5 backdrop-blur-md drift-in"
      data-testid="loyalty-tips-banner"
    >
      <div className="text-[10px] tracking-[0.4em] text-[#39FF14] mb-1">
        LOYALTY TIPS · PENDING {tips.length >= 3 && <span className="prismatic-text"> · STREAK ×{tips.length}</span>}
      </div>
      <div className="text-xs text-white/85 tracking-wider mb-2">
        Prismatic buyers rippled <span className="text-[#39FF14] font-bold tabular-nums">+{total}</span>{" "}
        chips back to you across <span className="text-white">{tips.length}</span>{" "}
        {tips.length === 1 ? "sale" : "sales"}
        {tips.length >= 3 && <span className="text-[#FF6EC7]"> · CLAIM TO ARM +5% BOOST</span>}
      </div>
      <button
        data-testid="loyalty-tips-claim"
        onClick={claim}
        disabled={claiming}
        className="text-[10px] tracking-[0.3em] px-3 py-1 border border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14]/10 transition font-bold"
      >
        {claiming ? "CLAIMING..." : "CLAIM ALL"}
      </button>
    </div>
  );
}


// ============================================================
// OUTBID REFUNDS BANNER — chips owed back to bidders who were outbid
// ============================================================
function OutbidRefundsBanner() {
  const [total, setTotal] = React.useState(0);
  const [count, setCount] = React.useState(0);
  const [claiming, setClaiming] = React.useState(false);

  React.useEffect(() => {
    const email = getMenuEmail();
    if (!email) return;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/gems/auctions/outbids?email=${encodeURIComponent(email)}`)
      .then((r) => { setTotal(r.data?.total || 0); setCount((r.data?.refunds || []).length); })
      .catch(() => {});
  }, []);

  const claim = async () => {
    const email = getMenuEmail();
    if (!email || claiming || total <= 0) return;
    setClaiming(true);
    sfx.click();
    try {
      const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/gems/auctions/outbids-claim`, { bidder_email: email });
      const paid = r.data?.refunded || 0;
      const sniped = r.data?.sniped_count || 0;
      const apology = r.data?.apology_tips || 0;
      if (paid > 0) {
        addChipsMenu(paid);
        if (sniped > 0) {
          toast.success(`SNIPED × ${sniped} · +${paid} refunded (incl. +${apology} apology tip)`, { duration: 6000 });
        } else {
          toast.success(`OUTBID REFUND · +${paid} chips returned · ${count} auction${count > 1 ? "s" : ""}`);
        }
      }
      setTotal(0);
      setCount(0);
    } catch {
      toast.error("REFUND CLAIM FAILED");
    } finally {
      setClaiming(false);
    }
  };

  if (total <= 0) return null;

  return (
    <div
      className="border border-[#F0ABFC]/50 p-3 bg-[#F0ABFC]/5 backdrop-blur-md drift-in"
      data-testid="outbid-refunds-banner"
    >
      <div className="text-[10px] tracking-[0.4em] text-[#F0ABFC] mb-1">OUTBID · REFUNDS WAITING</div>
      <div className="text-xs text-white/85 tracking-wider mb-2">
        You were outbid on <span className="text-white">{count}</span>{" "}
        {count === 1 ? "auction" : "auctions"} · <span className="text-[#F0ABFC] font-bold tabular-nums">+{total}</span> chips owed
      </div>
      <button
        data-testid="outbid-refunds-claim"
        onClick={claim}
        disabled={claiming}
        className="text-[10px] tracking-[0.3em] px-3 py-1 border border-[#F0ABFC] text-[#F0ABFC] hover:bg-[#F0ABFC]/10 transition font-bold"
      >
        {claiming ? "CLAIMING..." : "CLAIM REFUNDS"}
      </button>
    </div>
  );
}


// ============================================================
// AUCTION WATCH REMINDER — reminds you 15m before a watched auction closes
// ============================================================
function AuctionWatchReminder({ onOpenTrade }) {
  const [reminders, setReminders] = React.useState([]);
  const notifiedRef = React.useRef(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/gems/auctions?limit=50`);
        if (cancelled) return;
        const auctions = r.data?.auctions || [];
        const { pendingReminders, markReminded, pruneWatchlist } = await import("@/lib/auctionWatchlist");
        pruneWatchlist(auctions.map((a) => a.id));
        const rem = pendingReminders(auctions);
        setReminders(rem);
        if (rem.length && !notifiedRef.current) {
          notifiedRef.current = true;
          const first = rem[0];
          const mins = Math.max(1, Math.round(first.remainingMs / 60000));
          toast.info(`WATCHLIST · ${first.gem?.toUpperCase() || "GEM"} auction closes in ${mins}m`, { duration: 6000 });
          markReminded(rem.map((x) => x.id));
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  if (reminders.length === 0) return null;

  return (
    <div
      className="border border-[#FFD400]/50 p-3 bg-[#FFD400]/5 backdrop-blur-md drift-in"
      data-testid="auction-watch-reminder"
    >
      <div className="text-[10px] tracking-[0.4em] text-[#FFD400] mb-1">
        WATCHLIST · CLOSING SOON ({reminders.length})
      </div>
      <div className="text-xs text-white/85 tracking-wider mb-2">
        {reminders.slice(0, 3).map((r, i) => {
          const mins = Math.max(1, Math.round(r.remainingMs / 60000));
          return (
            <div key={r.id} className="font-mono flex justify-between">
              <span>{(r.gem || "gem").toUpperCase()} · top {r.top_bid || r.start_bid}</span>
              <span className="text-[#FFD400] tabular-nums">{mins}m</span>
            </div>
          );
        })}
      </div>
      <button
        data-testid="auction-watch-open"
        onClick={() => { sfx.click(); if (onOpenTrade) onOpenTrade(); }}
        className="text-[10px] tracking-[0.3em] px-3 py-1 border border-[#FFD400] text-[#FFD400] hover:bg-[#FFD400]/10 transition font-bold"
      >
        OPEN TRADE HALL →
      </button>
    </div>
  );
}


// ============================================================
// SNIPE COUNTER CARD — lifetime snipes made/taken with a SNIPE HALL toggle
// ============================================================
function SnipeCounterCard() {
  const [summary, setSummary] = React.useState(null);
  const [showHall, setShowHall] = React.useState(false);
  const [boostArmed, setBoostArmed] = React.useState(false);
  const gemSym = { crown: "♛", fire: "▲", star: "★", prismatic: "◈" };

  React.useEffect(() => {
    const email = getMenuEmail();
    if (!email) return;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/snipes/summary?email=${encodeURIComponent(email)}`)
      .then(async (r) => {
        const data = r.data || null;
        setSummary(data);
        if (data && (data.snipes_today || 0) >= 3) {
          const { maybeArmSnipeBoost, isSnipeBoostActive } = await import("@/lib/snipeBoost");
          const res = maybeArmSnipeBoost(data.snipes_today);
          if (res.armed) toast.success(`◆ SNIPE STREAK ×${data.snipes_today} · +10% CHIP BOOST · 6H`, { duration: 6500 });
          setBoostArmed(isSnipeBoostActive());
        } else {
          const { isSnipeBoostActive } = await import("@/lib/snipeBoost");
          setBoostArmed(isSnipeBoostActive());
        }
      })
      .catch(() => setSummary(null));
  }, []);

  if (!summary || (summary.made === 0 && summary.taken === 0)) return null;

  return (
    <div
      className="border border-[#FF6030]/40 p-3 bg-[#FF6030]/5 backdrop-blur-md drift-in"
      data-testid="snipe-counter-card"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] tracking-[0.4em] text-[#FF6030]">SNIPE COUNTER</div>
        <div className="flex items-center gap-2">
          {boostArmed && (
            <span
              data-testid="snipe-boost-pill"
              className="text-[9px] tracking-[0.3em] px-2 py-[2px] border border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10"
              title="+10% chip boost for the next 6 hours"
            >
              +10% · 6H
            </span>
          )}
          <button
            data-testid="snipe-hall-toggle"
            onClick={() => { sfx.click(); setShowHall((v) => !v); }}
            className="text-[9px] tracking-[0.3em] px-2 py-[2px] border border-[#FF6030]/60 text-[#FF6030] hover:bg-[#FF6030]/10 transition"
          >
            {showHall ? "HIDE" : "SNIPE HALL"}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 text-xs font-mono">
        <div>
          <div className="text-[9px] tracking-[0.3em] text-white/50">MADE</div>
          <div className="text-[#39FF14] font-bold tabular-nums">{summary.made}</div>
          <div className="text-[9px] text-white/50 tabular-nums">{summary.made_amount} chips</div>
        </div>
        <div>
          <div className="text-[9px] tracking-[0.3em] text-white/50">TAKEN</div>
          <div className="text-[#FF6030] font-bold tabular-nums">{summary.taken}</div>
          <div className="text-[9px] text-white/50 tabular-nums">{summary.taken_amount} refunded</div>
        </div>
      </div>
      {showHall && Array.isArray(summary.recent) && summary.recent.length > 0 && (
        <ol className="mt-3 space-y-1 max-h-40 overflow-y-auto" data-testid="snipe-hall-list">
          {summary.recent.map((e, i) => (
            <li key={i} className="flex items-center justify-between text-[10px] font-mono border-b border-white/5 pb-1">
              <span className="flex items-center gap-2">
                <span style={{ color: e.kind === "made" ? "#39FF14" : "#FF6030" }}>
                  {e.kind === "made" ? "▲" : "▼"}
                </span>
                <span className="text-white/70">{gemSym[e.gem] || "•"} {e.gem?.toUpperCase()}</span>
              </span>
              <span className={`tabular-nums ${e.kind === "made" ? "text-[#39FF14]" : "text-[#FF6030]"}`}>
                {e.kind === "made" ? "+" : "-"}{e.amount}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}


// ============================================================
// SKIN LOADOUT BAR — up to 3 named { skin + gem } quick-swap presets
// ============================================================
function SkinLoadoutBar() {
  const [loadouts, setLoadouts] = React.useState([]);
  const [active, setActive] = React.useState(-1);
  const [editIdx, setEditIdx] = React.useState(-1);
  const [name, setName] = React.useState("");

  const refresh = () => {
    (async () => {
      const mod = await import("@/lib/skinLoadouts");
      setLoadouts(mod.getLoadouts());
      setActive(mod.activeLoadoutIndex());
    })();
  };
  React.useEffect(() => { refresh(); }, []);

  const activate = async (i) => {
    const mod = await import("@/lib/skinLoadouts");
    const res = mod.applyLoadout(i);
    if (res.ok) {
      sfx.click();
      toast.success(`LOADOUT ${i + 1} · ${res.slot.name}`);
      refresh();
    }
  };

  const saveHere = async (i) => {
    const mod = await import("@/lib/skinLoadouts");
    const skinId = localStorage.getItem("ppe_current_skin_id") || null;
    const gemId = localStorage.getItem("ppe_handle_gem_v1") || "";
    mod.saveLoadout(i, { name: name || `SLOT ${i + 1}`, skinId, gemId });
    sfx.click();
    toast.success(`LOADOUT ${i + 1} · saved current skin + gem`);
    setEditIdx(-1);
    setName("");
    refresh();
  };

  return (
    <div className="flex items-center gap-2 my-3 flex-wrap" data-testid="skin-loadout-bar">
      <span className="text-[10px] tracking-[0.4em] text-white/40">LOADOUTS ·</span>
      {[0, 1, 2].map((i) => {
        const slot = loadouts[i];
        const filled = slot && slot.skinId;
        const isActive = active === i;
        return (
          <div key={i} className="flex items-center gap-1">
            <button
              onClick={() => (filled ? activate(i) : setEditIdx(i))}
              data-testid={`skin-loadout-${i}`}
              className={`text-[10px] tracking-[0.3em] px-2 py-1 border transition ${isActive ? "border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10" : filled ? "border-white/30 text-white/70 hover:border-white/60 hover:text-white" : "border-white/10 text-white/30 hover:text-white/60 border-dashed"}`}
              title={filled ? `${slot.name}${isActive ? " · active" : ""}` : "Empty slot — click to save current"}
            >
              {filled ? `${isActive ? "★ " : ""}${slot.name}` : `+ SLOT ${i + 1}`}
            </button>
            {filled && (
              <button
                onClick={async () => {
                  const mod = await import("@/lib/skinLoadouts");
                  mod.deleteLoadout(i);
                  toast.info(`LOADOUT ${i + 1} · cleared`);
                  refresh();
                }}
                data-testid={`skin-loadout-clear-${i}`}
                className="text-[10px] text-white/30 hover:text-white/60"
                title="Clear slot"
              >
                ×
              </button>
            )}
            {editIdx === i && (
              <>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value.slice(0, 12))}
                  placeholder={`name (${i + 1})`}
                  data-testid={`skin-loadout-name-${i}`}
                  className="bg-black/40 border border-white/20 text-white text-[10px] px-1 py-[2px] w-20"
                />
                <button
                  onClick={() => saveHere(i)}
                  data-testid={`skin-loadout-save-${i}`}
                  className="text-[10px] tracking-[0.3em] px-2 py-[2px] border border-[#F0ABFC] text-[#F0ABFC] hover:bg-[#F0ABFC]/10"
                >
                  SAVE
                </button>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

